import { ChatApiResponse } from "../types";
import { PARROT_API_BASE_URL } from "../config";

/** Default (and only) chat endpoint. Baked in — see `PARROT_API_BASE_URL`. */
export const PARROT_CHAT_ENDPOINT = `${PARROT_API_BASE_URL}/agents/chat`;

export interface SendMessageArgs {
	endpoint: string;
	agentType: string;
	query: string;
	sessionId?: string;
	authToken?: string;
	headers?: Record<string, string>;
	signal?: AbortSignal;
	/** Consumer-supplied fields (`ParrotChat` `requestBody`). Can't override the core fields. */
	body?: Record<string, unknown>;
}

/** Fields the widget owns; a consumer's `requestBody` never overrides these. */
const RESERVED_BODY_KEYS = ["agent_type", "query", "session_id"];

const consumerBody = (body?: Record<string, unknown>): Record<string, unknown> => {
	if (!body) return {};
	const out: Record<string, unknown> = {};
	for (const [k, v] of Object.entries(body)) if (!RESERVED_BODY_KEYS.includes(k)) out[k] = v;
	return out;
};

export interface SendMessageResult {
	content: string;
	sessionId?: string;
	/** Full parsed response body, for persistence as raw_response. */
	raw?: unknown;
}

/** Shared by the JSON and stream transports; `extraBody` carries transport-specific fields. */
export const buildRequestInit = (
	{ agentType, query, sessionId, authToken, headers, signal, body }: SendMessageArgs,
	accept: string,
	extraBody: Record<string, unknown> = {}
): RequestInit => ({
	method: "POST",
	headers: {
		"Content-Type": "application/json",
		Accept: accept,
		...(authToken ? { "x-auth-token": authToken } : {}),
		...(headers || {}),
	},
	body: JSON.stringify({
		...consumerBody(body),
		agent_type: agentType,
		query,
		...(sessionId ? { session_id: sessionId } : {}),
		...extraBody,
	}),
	signal,
});

export async function sendMessage(args: SendMessageArgs): Promise<SendMessageResult> {
	const res = await fetch(args.endpoint, buildRequestInit(args, "application/json"));

	if (!res.ok) {
		throw new Error(`Chat request failed: ${res.status} ${res.statusText}`);
	}

	const data = (await res.json()) as ChatApiResponse;
	if (data.error) throw new Error(data.error);

	const content =
		typeof data.response === "string"
			? data.response
			: typeof (data as { result?: unknown }).result === "string"
				? ((data as { result: string }).result)
				: typeof (data as { raw_response?: unknown }).raw_response === "string"
					? ((data as { raw_response: string }).raw_response)
					: "";

	return { content, sessionId: data.session_id, raw: data };
}
